import { ChangeDetectionStrategy, Component, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MdlDialogReference, MdlDialogService } from '@angular-mdl/core';
import { AdminEQueryEscalationsStorage } from '../../admin-e-query-escalations-storage';
import { Accession } from '../../../shared/accession';
import { BehaviorSubject, Observable } from 'rxjs';
import { CUSTOM_ACCESSIONS_STORAGE } from '../../../shared';
import { AdminEQueryEscalationSearchFieldsStorage } from '../../admin-e-query-escalation-search-fields-storage';
import { maxLengthInBytes, maxBytesError } from '../../../../shared/validators';
import { map } from 'rxjs/operators';
import * as stream from 'stream';

@Component({
    selector: 'new-escalation-dialog',
    template: require('./new-escalation-dialog.component.html'),
    styles: [require('./new-escalation-dialog.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: CUSTOM_ACCESSIONS_STORAGE, useClass: AdminEQueryEscalationSearchFieldsStorage }
    ]
})
export class NewEscalationDialogComponent {
    public readonly reasonMaxLength = 250;
    public readonly accessionControl = new FormControl(null, [Validators.required]);
    public readonly emailControl = new FormControl(null, [Validators.required]);
    public readonly escalationLevelControl = new FormControl(null, [Validators.required]);
    public readonly reasonControl = new FormControl(null, [Validators.required, maxLengthInBytes(this.reasonMaxLength)]);
    public readonly formGroup = new FormGroup({
        accession: this.accessionControl,
        emailControl: this.emailControl,
        escalationLevel: this.escalationLevelControl,
        reason: this.reasonControl,
    });
    public readonly errors$ = new BehaviorSubject<string[]>(null);
    public accessionNumber$: Observable<stream>;

    constructor(
        private readonly dialog: MdlDialogReference,
        private readonly dialogService: MdlDialogService,
        private readonly adminEscalationsStorage: AdminEQueryEscalationsStorage,
        private readonly changeDetectorRef: ChangeDetectorRef,
    ) {
        this.accessionNumber$ = this.accessionControl.valueChanges.pipe(
            map(v => v.accessionNumber)
        );
    }

    public get errorMessage() {
        if (this.reasonControl.hasError('required')) {
            return 'Reason is required';
        }
        if (this.reasonControl.hasError(maxBytesError)) {
            return `Maximum length is ${this.reasonMaxLength}`;
        }
        return '';
    }

    public async onAdd() {
        if (this.formGroup.invalid) {
            return;
        }

        const accession: Accession = this.accessionControl.value;
        const confirmed = await this.showConfirm(`The ${this.emailControl.value} will be added to ${accession.accessionNumber}. Proceed?`);

        if (!confirmed) {
            return;
        }

        this.formGroup.disable();
        this.changeDetectorRef.markForCheck();

        try {
            const response = await this.adminEscalationsStorage.createEscalation({
                accessionId: accession.accessionId,
                email: this.emailControl.value,
                escalationLevel: this.escalationLevelControl.value,
                reason: this.reasonControl.value,
            });

            if (response.errors.length) {
                this.errors$.next(response.errors);
            } else {
                this.dialog.hide();
            }

            this.adminEscalationsStorage.reloadEscalations();
        } finally {
            this.formGroup.enable();
            this.changeDetectorRef.markForCheck();
        }
    }

    public onCancel() {
        this.dialog.hide();
    }

    private async showConfirm(confirmationText: string): Promise<boolean> {
        try {
            await this.dialogService.confirm(confirmationText, 'No', 'Yes').toPromise();
            return true;
        } catch {
            return false;
        }
    }
}
